/**
 * BYO v2 — focused CODE-node edit modal.
 *
 * Opened from the flows canvas when a user edits the CODE node of a BYO
 * (`flow_type: custom`) flow (CodeNode dispatches edit with
 * `queryParams.byoSection === 'code'`). Renders as the CONTENT of the
 * fullscreen MUI Dialog owned by `ModalSourceForm`.
 *
 * Renders the SAME Studio processor UI as create ({@link StudioCodeStep}:
 * "Use Existing Function" / "Provide Your Own Function", editor + Docs +
 * Packages/Variables + test panel), hydrated from the existing source id via
 * {@link useByoEditSource}. On Save it updates the code container and calls
 * `onDismiss(updatedSource)`.
 *
 * `queryParams.viewOnly` (BYO pending-issues #7 — "view code without
 * entering edit mode", from CodeNode's ⋯ menu "View code" item) skips all
 * of that and shows just the decoded code in a read-only editor with a
 * single Close button — no Save, no Packages/Variables/Test panel, since
 * none of those make sense for a pure view.
 *
 * The connector/directory (source) node is edited by ByoSourceSetupForm, not
 * here — this modal is code-only.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import Constants from 'js/Constants';
import { StudioCodeStep } from 'new/components/flows/types/byo/v2-shells/studio/StudioCodeStep';
import { isPackageReqValid } from 'new/components/flows/types/byo/v2-shells/studio/PackagesTable';
import { serializeRuntimeVars } from 'new/components/flows/types/byo/core/model';
import { ConfirmDialog } from 'new/elements/ConfirmDialog';
import { ByoModalHeader } from './ByoModalHeader';
import { useByoEditSource } from './useByoEditSource';
import { getSaveBlockedReason } from './useByoSourceSave';

export interface ByoEditCodeModalProps {
  /** Data source id — BYO code always lives on the source. */
  id?: number;
  onDismiss?: (source: any) => void;
  queryParams?: any;
}

export function ByoEditCodeModal({ id, onDismiss, queryParams }: ByoEditCodeModalProps) {
  const viewOnly = !!queryParams?.viewOnly;
  const draft = useByoEditSource(id, { viewOnly });

  // A pinned `==`/`>=` package with no version is invalid — Save used to
  // persist e.g. `pandas==` (NEX-22636). Derived from the draft's list, not
  // reported by PackagesTable: that table unmounts with its popover, so a list
  // replaced while it's closed (Load as Template) was never re-validated.
  const packagesValid = draft.packages.every(isPackageReqValid);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // extraData was hardcoded to '' here, so testing silently ran against no
  // runtime variables even after the user configured them in the Variables
  // panel. serializeRuntimeVars is the same serializer useByoSourceSave uses
  // for the real save — testing against that is testing what's about to be
  // persisted, which is the only value we can vouch for; a source last saved
  // by an older/legacy code path could have extra_data in a different wire
  // shape than what a fresh save would now produce.
  //
  // A "Use Existing Function" pick test-runs the PICKED container's own
  // code/packages/extra_data (what the flow will actually run), kept apart
  // from the own-code draft so a pick never overwrites it (NEX-22636).
  const isPredefined = draft.codeMode === 'predefined';
  const testCode = useMemo(
    () => ({
      mode: draft.codeMode,
      code: isPredefined ? draft.predefinedCode : draft.code,
      predefinedId: draft.predefinedId,
      packages: isPredefined ? draft.predefinedPackages : draft.packages,
      extraData: isPredefined ? draft.predefinedExtraData : serializeRuntimeVars(draft.runtimeVars),
    }),
    [
      isPredefined,
      draft.codeMode,
      draft.code,
      draft.predefinedCode,
      draft.predefinedId,
      draft.packages,
      draft.predefinedPackages,
      draft.predefinedExtraData,
      draft.runtimeVars,
    ],
  );

  // Why Save can't run right now (null = it can). Same rules save() enforces,
  // plus package validity — only the modal holds the editable package list.
  // Packages only gate own-code saves: a predefined pick is saved by reference.
  const saveBlockedReason =
    !isPredefined && !packagesValid
      ? 'Fix the invalid packages first'
      : getSaveBlockedReason(draft);

  const onSave = async () => {
    const updated = await draft.save({ markCodeConfigured: true });
    // Surface the save error inline; only dismiss when the update succeeded.
    if (updated) onDismiss?.(updated);
  };

  // Browser back / refresh are already confirmed by ModalSourceForm's
  // UserConfirmationForModals; this covers the in-modal Cancel button.
  const onCancel = () => {
    if (!viewOnly && draft.isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onDismiss?.(null);
  };

  return (
    <Box
      data-testid="byo-edit-code-modal"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'var(--color-white, #fff)',
      }}
    >
      <ByoModalHeader
        title={viewOnly ? 'View code' : draft.codeConfigured ? 'Edit code' : 'Configure code'}
        subtitle={draft.name || undefined}
        saving={draft.isSaving}
        // processorPickPending: a "Use Existing Function" pick was just made
        // and its code hasn't been fetched + decoded yet — Save must wait,
        // or it persists whatever `code`/`packages` were left over from
        // before the pick under the new pick's id. Confirmed bug, fixed
        // 2026-08-20 — see UseByoEditSource.processorPickPending.
        saveDisabled={
          draft.loading ||
          draft.processorPickPending ||
          draft.hydrationFailed ||
          saveBlockedReason != null
        }
        saveDisabledReason={saveBlockedReason}
        onSave={() => void onSave()}
        onCancel={onCancel}
        readOnly={viewOnly}
        data-testid="byo-edit-code-header"
      />

      {draft.error ? (
        <Alert severity="error" sx={{ m: 2 }} data-testid="byo-edit-code-error">
          {draft.error}
        </Alert>
      ) : null}
      {draft.credentialError ? (
        <Alert severity="warning" sx={{ m: 2 }} data-testid="byo-edit-code-credential-warning">
          {draft.credentialError}
        </Alert>
      ) : null}
      {!viewOnly && draft.pickError ? (
        <Alert severity="error" sx={{ m: 2 }} data-testid="byo-edit-code-pick-error">
          {draft.pickError}
        </Alert>
      ) : null}
      {!viewOnly && draft.saveError ? (
        <Alert severity="error" sx={{ m: 2 }} data-testid="byo-edit-code-save-error">
          {draft.saveError}
        </Alert>
      ) : null}

      {draft.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress data-testid="byo-edit-code-loading" />
        </Box>
      ) : viewOnly ? (
        <Box
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2 }}
          data-testid="byo-view-code"
        >
          <LoadableMonacoEditor
            language={Constants.CodeLanguages.PYTHON}
            theme="dark"
            height="100%"
            options={{ readOnly: true, minimap: { enabled: false } }}
            value={draft.code}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
          <StudioCodeStep
            code={draft.code}
            onCodeChange={draft.setCode}
            codeMode={draft.codeMode}
            onCodeModeChange={draft.setCodeMode}
            predefinedId={draft.predefinedId}
            onPredefinedSelected={draft.setPredefinedId}
            packages={draft.packages}
            onPackagesChange={draft.setPackages}
            runtimeVars={draft.runtimeVars}
            onRuntimeVarsChange={draft.setRuntimeVars}
            performanceConfig={draft.performanceConfig}
            onPerformanceConfigChange={draft.setPerformanceConfig}
            testCode={testCode}
            sourceConfig={draft.sourceConfig}
            credentialId={Number(draft.input.credentialId) || 0}
            folderPath={draft.input.folderPath}
          />
        </Box>
      )}

      <ConfirmDialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        onConfirm={() => onDismiss?.(null)}
        title="Discard unsaved changes?"
        message="You have unsaved changes to this code. If you leave now, they will be lost."
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
      />
    </Box>
  );
}

export default ByoEditCodeModal;
